/**
 * AppICEMAP — Geocoding service
 * Coordinate → address (reverse) and address → coordinates (search), both on
 * the OpenStreetMap Nominatim API. No API key needed.
 *
 * Nominatim is a shared community resource with a strict 1 req/s policy, so we
 * cache, throttle every call through the same gate, and abort stale requests.
 */

const NOMINATIM_REVERSE_URL = "https://nominatim.openstreetmap.org/reverse";
const NOMINATIM_SEARCH_URL = "https://nominatim.openstreetmap.org/search";

/** Minimum gap between requests (Nominatim usage policy: 1 req/s) */
const MIN_GAP_MS = 1200;

/** @type {Map<string, {name: string, area: string}>} cache by rounded coord */
const cache = new Map();

/** @type {number} timestamp of the last request of ANY kind */
let lastRequestAt = 0;

/** @type {AbortController | null} in-flight reverse lookup */
let inFlight = null;

/** @type {AbortController | null} in-flight address search */
let searchInFlight = null;

/**
 * Wait out the shared 1 req/s gate. Both endpoints go through it, so a reverse
 * lookup and a search can never fire back to back.
 */
async function throttle() {
	const wait = MIN_GAP_MS - (Date.now() - lastRequestAt);
	if (wait > 0) await new Promise((r) => setTimeout(r, wait));
	lastRequestAt = Date.now();
}

/**
 * Round a coordinate to ~11m so small GPS jitter reuses the cached label.
 * @param {number} lat
 * @param {number} lng
 * @returns {string}
 */
function cacheKey(lat, lng) {
	return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

/**
 * Reverse geocode a coordinate.
 * Never throws — returns null when unavailable (offline, rate-limited, aborted).
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<{name: string, area: string} | null>}
 */
export async function reverseGeocode(lat, lng) {
	const key = cacheKey(lat, lng);
	if (cache.has(key)) return cache.get(key);

	await throttle();

	// Abort any previous lookup — only the newest position matters
	if (inFlight) inFlight.abort();
	inFlight = new AbortController();

	const params = new URLSearchParams({
		lat: String(lat),
		lon: String(lng),
		format: "jsonv2",
		zoom: "18",
		addressdetails: "1",
		"accept-language": "es",
	});

	try {
		const res = await fetch(`${NOMINATIM_REVERSE_URL}?${params}`, {
			signal: inFlight.signal,
			headers: { Accept: "application/json" },
		});
		if (!res.ok) return null;

		const data = await res.json();
		const label = toLabel(data);
		if (label) cache.set(key, label);
		return label;
	} catch (err) {
		if (err.name !== "AbortError") {
			console.warn("[Geocode] Reverse lookup failed:", err.message);
		}
		return null;
	} finally {
		inFlight = null;
	}
}

/**
 * Reduce a Nominatim response to a title + area pair.
 * @param {object} data
 * @returns {{name: string, area: string} | null}
 */
function toLabel(data) {
	const a = data?.address;
	if (!a) return null;

	const name =
		data.name ||
		a.amenity ||
		a.building ||
		a.road ||
		a.neighbourhood ||
		a.suburb ||
		a.city ||
		a.town ||
		a.village ||
		"";

	const city = a.city || a.town || a.village || a.county || a.state || "";
	const country = a.country || "";
	const area = [city, country].filter(Boolean).join(", ");

	if (!name && !area) return null;
	return { name: name || area, area: name ? area : "" };
}

/* ── Address search ──────────────────────────────────────────────────────── */

/**
 * Search an address and return candidate places.
 *
 * Nominatim's free-form search is brittle with "calle, localidad, estado CP"
 * when the locality is not what OSM has on the street (e.g. "111 Herrod Blvd,
 * Dayton, NJ 08810" returns nothing because OSM files that street under South
 * Brunswick). So we retry with progressively looser queries instead of leaving
 * the user staring at "sin resultados" for an address that does exist.
 *
 * Never throws — returns [] when unavailable.
 *
 * @param {string} query - Free-form address
 * @param {number} [limit=5]
 * @returns {Promise<Array<{lat: number, lng: number, label: string}>>}
 */
export async function searchAddress(query, limit = 5) {
	const q = (query || "").trim();
	if (q.length < 3) return [];

	for (const attempt of queryVariants(q)) {
		const results = await runSearch(attempt, limit);
		if (results.length > 0) return results;
	}

	return [];
}

/**
 * Progressively looser variants of an address query.
 * @param {string} q
 * @returns {string[]}
 */
function queryVariants(q) {
	const variants = [q];
	const parts = q
		.split(",")
		.map((p) => p.trim())
		.filter(Boolean);

	// Drop the locality (2nd component): the usual culprit when a street sits in
	// a differently-named municipality in OSM.
	if (parts.length >= 3) {
		variants.push([parts[0], ...parts.slice(2)].join(", "));
	}

	// Street plus postcode only
	const postcode = q.match(/\b\d{4,5}(?:-\d{4})?\b/)?.[0];
	if (parts.length >= 2 && postcode && !parts[0].includes(postcode)) {
		variants.push(`${parts[0]}, ${postcode}`);
	}

	// Last resort: just the street/number
	if (parts.length >= 2) variants.push(parts[0]);

	return [...new Set(variants)];
}

/**
 * One Nominatim search call.
 * @param {string} q
 * @param {number} limit
 * @returns {Promise<Array<{lat: number, lng: number, label: string}>>}
 */
async function runSearch(q, limit) {
	await throttle();

	if (searchInFlight) searchInFlight.abort();
	searchInFlight = new AbortController();

	const params = new URLSearchParams({
		q,
		format: "json",
		limit: String(limit),
		addressdetails: "1",
		// La app cubre solo Estados Unidos: sin esto salen calles homónimas de
		// otros países que luego no se podrían usar para reportar.
		countrycodes: "us",
	});

	try {
		const res = await fetch(`${NOMINATIM_SEARCH_URL}?${params}`, {
			signal: searchInFlight.signal,
			headers: { Accept: "application/json" },
		});
		if (!res.ok) return [];

		const data = await res.json();
		if (!Array.isArray(data)) return [];

		return data.map((r) => ({
			lat: parseFloat(r.lat),
			lng: parseFloat(r.lon),
			label: r.display_name || "",
		}));
	} catch (err) {
		if (err.name !== "AbortError") {
			console.warn("[Geocode] Address search failed:", err.message);
		}
		return [];
	} finally {
		searchInFlight = null;
	}
}

/* ── Country check ───────────────────────────────────────────────────────── */

/**
 * Which country is this coordinate in, per OpenStreetMap.
 *
 * The offline polygon in utils/geo.js is good to a few kilometres, which is not
 * enough where a border runs through a city — Detroit and Windsor sit 2 km
 * apart across the river. This is the precise check, used at the moment a
 * report is published.
 *
 * @param {number} lat
 * @param {number} lng
 * @returns {Promise<string|null>} ISO code in lower case ("us"), or null if the
 *   lookup is unavailable — callers must fall back to the offline check.
 */
export async function getCountryCode(lat, lng) {
	await throttle();

	const params = new URLSearchParams({
		lat: String(lat),
		lon: String(lng),
		format: "jsonv2",
		zoom: "3",
		addressdetails: "1",
	});

	try {
		const res = await fetch(`${NOMINATIM_REVERSE_URL}?${params}`, {
			headers: { Accept: "application/json" },
		});
		if (!res.ok) return null;

		const data = await res.json();
		const code = data?.address?.country_code;
		return typeof code === "string" ? code.toLowerCase() : null;
	} catch (err) {
		console.warn("[Geocode] Country lookup failed:", err.message);
		return null;
	}
}
